import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'

type ApiValue = {
  count: number
  min: number
  max: number
  values: number[]
}

/**
 * 0 - training mode inactive
 * 2 - training mode active
 */
export type BatteryTrainingMode = 0 | 2

export interface BatteryOverviewDto {
  batIndex: number
  batDeviceState: {
    batDeviceConnected: boolean
    batDeviceWorking: boolean
    batDeviceInService: boolean
  }
  batCurrent: ApiValue
  batPower: ApiValue
  batTemperature: ApiValue
  batVoltage: ApiValue
  batRsoc: number
  batAsoc: number
  batFcc: number
  batRc: number
  batDcbCount: number
  batChargeCycles: number
  batMaxBatVoltage: number
  batMaxChargeCurrent: number
  batEodVoltage: number
  batMaxDischargeCurrent: number
  batTerminalVoltage: number
  batStatusCode: string
  batErrorCode: string
  batDeviceName: string
  batFirmwareVersion: string
  batParamBatNumber: number
  batCellVoltage: number[]
  batDcbType: string
  batIsBreakerOpen: boolean
  batBatterySoftOn: boolean
  batVoltAdjustment: {
    status: number
    target?: number
    min?: number
    max?: number
  }
  batSeCount: number
  batBri: {
    paramIndex: number
    paramUint: number
    paramBool: boolean
  }
  batTrainingMode: BatteryTrainingMode
  batDesignCapacity: number
}

type BatteryVoDto = {
  battvoBattype?: string
  battvoRemainingCapacity: number
  battvoRemainingRoundTripEfficiency: number
  battvoEvolutionOfSelfDischargeRates: number
  battvoOhmicResistance: number
  battvoDateOfManufacture: string
  battvoEnergyThroughput: number

  /** optional for now, because rscp does not deliver it yet */
  battvoRemainingPowerCapabilityCharge?: number
  /** optional for now, because rscp does not deliver it yet */
  battvoRemainingPowerCapabilityDischarge?: number

  /** ISO date */
  battvoDatePuttingIntoService?: string
  battvoHarmfulEventsDeepDischargeEvents: number
  battvoHarmfulEventsTimeTempExtremeHigh: number
  battvoHarmfulEventsTimeTempExtremeHighCharge: number
  battvoFullEquivalentChargeDischargeCycles: number
  battvoCapacityThroughput: number
  battvoHarmfulEventsOverchargeEvents: number
  battvoHarmfulEventsTimeTempExtremeLowCharge: number
  battvoHarmfulEventsTimeDeepDischarge: number
  battvoHarmfulEventsTimeOvercharge: number
  battvoHarmfulEventsTimeTempExtremeLow: number
  battvoHarmfulEventsTimeOvercurrentDischarge: number
  battvoHarmfulEventsTimeOvercurrentCharge: number
}

export const getStorageBatteryOverview = async (
  storageHardwareDeviceId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<(Nullable<BatteryOverviewDto> & { storageHardwareDeviceId?: string }) | null>> => {
  try {
    const response = await authFetch<Nullable<BatteryOverviewDto> | null>({
      path: `storages/${storageHardwareDeviceId}/hardware/batteries/overview/0`,
    })

    if (response.status !== StatusCodes.OK || response.data === null) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult({ ...response.data, storageHardwareDeviceId })
  } catch (error) {
    console.error('Error fetching storage battery overview', error)
    // Original function catches and returns null, so we return null on error
    return new ApiResult<null>(null)
  }
}

/**
 * Currently not used
 * @deprecated
 */
export const getStorageBatteryIds = async (storageId: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<{ ids: number[] } | null>> => {
  try {
    const response = await authFetch<{ ids: number[] }>({
      path: `storages/${storageId}/hardware/batteries/overview`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<{ ids: number[] }>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get storage battery IDs:')
  }
}

/**
 * Currently not used
 * @deprecated
 */
export const getStorageBattery = async (
  storageId: string,
  batteryId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<BatteryOverviewDto> | null>> => {
  try {
    const response = await authFetch<Nullable<BatteryOverviewDto>>({
      path: `storages/${storageId}/hardware/batteries/overview/${batteryId}`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<BatteryOverviewDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get storage battery:')
  }
}

export const getStorageBatteryVO = async (
  storageId: string,
  index: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<BatteryVoDto> | null>> => {
  try {
    const response = await authFetch<Nullable<BatteryVoDto>>({
      path: `storages/${storageId}/hardware/batteries/overview/${index}/battvo`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<BatteryVoDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get storage battery VO:')
  }
}
